/**
 * HmWarehouseReport — zestawienia magazynowe.
 *
 * Bliźniak ekranu zestawień sprzedaży: stan siedzi w adresie, koszyki liczy przeglądarka tą samą
 * funkcją co API, pozycje dociągane są paczkami z paskiem postępu.
 *
 * Czym się różni od sprzedaży — i dlaczego to nie jest ten sam ekran z inną nazwą: magazyn nie ma
 * VAT-u ani brutto, za to liczy się ILOŚĆ. Przy podsumowaniu wg towaru pytanie brzmi „ile sztuk
 * zeszło", a nie „za ile" — dlatego kolumna ilości i suma ilości pojawiają się w trybie pozycji.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  aggregateWarehouseReport,
  explodeWarehouseToPositions,
  HM_WAREHOUSE_REPORT_DIMENSIONS,
  hmWarehouseDimensionNeedsDetails,
  type HmWarehouseMoney,
  type HmWarehouseReportDetail,
  type HmWarehouseReportDimension,
  type HmWarehouseReportRow,
} from '@saas/shared';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Box, Download, Printer, RefreshCw, SlidersHorizontal } from 'lucide-react';

import { Button } from '@/components/Button';
import type { ColumnDefinition } from '@/components/configurable-table';
import { ConfigurableTable } from '@/components/configurable-table';
import { Dropdown } from '@/components/Dropdown';
import { WarehouseReportParamsModal } from '@/components/handel-magazyn/WarehouseReportParamsModal';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { downloadCsv } from '@/lib/handel-magazyn/reports-api';
import {
  chunkWarehouseIds,
  fetchWarehouseReport,
  fetchWarehouseReportDetails,
  isWarehouseReportSlug,
  parseWarehouseSearchParams,
  toWarehouseSearchParams,
  type WarehouseReportParams,
  warehouseReportTitleKey,
  warehouseReportToCsv,
} from '@/lib/handel-magazyn/warehouse-reports-api';
import { useActiveCompanyScope } from '@/lib/use-active-company';

const PRINT_PERMISSION = 'handel_magazyn.warehouse_documents.print';

export function HmWarehouseReport() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const companyId = useActiveCompanyScope().companyId;
  const { me } = useTenantAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { report: reportSlug } = useParams<{ report: string }>();

  const slug = isWarehouseReportSlug(reportSlug) ? reportSlug : 'magazyn-za-okres';
  const reportTitle = t(warehouseReportTitleKey(slug));
  const params = useMemo(() => parseWarehouseSearchParams(searchParams), [searchParams]);

  const canPrint =
    me?.user.isSuperUser === true || (me?.permissions.includes(PRINT_PERMISSION) ?? false);

  const [paramsOpen, setParamsOpen] = useState(false);
  const [details, setDetails] = useState<HmWarehouseReportDetail[]>([]);
  const [failed, setFailed] = useState<number[]>([]);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [printAll, setPrintAll] = useState(false);

  // Wymiar i ziarno nie wchodzą do klucza — zmieniają tylko sposób liczenia, który robimy u siebie.
  const dataKey = useMemo(() => {
    const sp = toWarehouseSearchParams(params);
    sp.delete('dim');
    sp.delete('grain');
    sp.delete('preset');
    return sp.toString();
  }, [params]);

  const query = useQuery({
    queryKey: ['handel-magazyn', 'warehouse-report', companyId, dataKey],
    enabled: !!companyId && !!params.dateFrom && !!params.dateTo,
    retry: false,
    queryFn: ({ signal }) => fetchWarehouseReport(companyId!, params, signal),
  });

  const report = query.data;

  const runId = useRef(0);
  useEffect(() => {
    setDetails([]);
    setFailed([]);
    setProgress(null);
    if (!report || !companyId) return;
    if (!report.params.details || report.detailsSkipped) return;

    const ids = Array.from(new Set(report.rows.map((r) => r.documentId)));
    if (ids.length === 0) return;

    const id = ++runId.current;
    const controller = new AbortController();
    setProgress({ done: 0, total: ids.length });

    void (async () => {
      const collected: HmWarehouseReportDetail[] = [];
      const missing: number[] = [];
      let done = 0;
      for (const chunk of chunkWarehouseIds(ids)) {
        try {
          const res = await fetchWarehouseReportDetails(companyId, chunk, controller.signal);
          collected.push(...res.details);
          missing.push(...res.failed);
        } catch (err) {
          if (controller.signal.aborted) return;
          missing.push(...chunk);
          const message = isAxiosError(err)
            ? ((err.response?.data as { message?: string } | undefined)?.message ?? undefined)
            : undefined;
          toast.show({
            variant: 'warning',
            title: t('tenant.handelMagazyn.reports.detailsChunkFailed'),
            description: message,
          });
        }
        done += chunk.length;
        if (runId.current !== id) return;
        setProgress({ done, total: ids.length });
      }
      if (runId.current !== id) return;
      setDetails(collected);
      setFailed(missing);
      setProgress(null);
    })();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, companyId]);

  const grain =
    params.positions || hmWarehouseDimensionNeedsDetails(params.dim) ? 'position' : 'document';
  const withPositions = grain === 'position';

  const rows = useMemo<HmWarehouseReportRow[]>(() => {
    if (!report) return [];
    const base = report.buckets ? report.buckets.flatMap((b) => b.rows) : report.rows;
    if (!withPositions) return base;
    // Bez pozycji nie ma czego rozbijać — do czasu ich dociągnięcia pokazujemy nagłówki.
    return details.length === 0 ? base : explodeWarehouseToPositions(base, details);
  }, [report, details, withPositions]);

  const aggregate = useMemo(
    () =>
      aggregateWarehouseReport({
        rows,
        dimension: params.dim,
        grain: withPositions && details.length > 0 ? 'position' : 'document',
      }),
    [rows, params.dim, withPositions, details.length],
  );

  const columns = useMemo<ColumnDefinition<HmWarehouseReportRow>[]>(() => {
    const base: ColumnDefinition<HmWarehouseReportRow>[] = [
      {
        id: 'documentNumber',
        header: t('tenant.handelMagazyn.columns.documentNumber'),
        accessor: (row) => row.documentNumber,
        defaultWidth: 170,
        renderCell: (_v: unknown, row: HmWarehouseReportRow) => (
          <Button
            variant="ghost"
            size="sm"
            className="h-auto justify-start px-0 py-0 font-mono text-xs font-normal text-text-primary underline-offset-2 hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/handel-magazyn/magazyn/${row.documentId}`);
            }}
          >
            {row.documentNumber}
          </Button>
        ),
      },
      {
        id: 'issueDate',
        header: t('tenant.handelMagazyn.reports.columns.issuedOn'),
        accessor: (row) => row.issueDate?.slice(0, 10) ?? '—',
        defaultWidth: 120,
      },
      {
        id: 'contractorName',
        header: t('tenant.handelMagazyn.columns.contractor'),
        accessor: (row) => row.contractorName ?? '—',
        defaultWidth: 220,
      },
      {
        id: 'warehouseName',
        header: t('tenant.handelMagazyn.warehouse.columns.warehouse'),
        accessor: (row) => row.warehouseName ?? '—',
        defaultWidth: 160,
      },
    ];

    if (withPositions) {
      base.push(
        {
          id: 'productCode',
          header: t('tenant.handelMagazyn.reports.columns.product'),
          accessor: (row) => row.productName ?? row.productCode ?? '—',
          defaultWidth: 240,
        },
        {
          id: 'quantity',
          header: t('tenant.handelMagazyn.reports.columns.quantity'),
          accessor: (row) => `${row.quantity ?? ''} ${row.unit ?? ''}`.trim(),
          align: 'right',
          defaultWidth: 110,
        },
      );
    }

    base.push(
      {
        id: 'currency',
        header: t('tenant.handelMagazyn.columns.currency'),
        accessor: (row) => row.currency,
        defaultWidth: 80,
      },
      {
        id: 'netValue',
        header: t('tenant.handelMagazyn.columns.net'),
        accessor: (row) => row.netValue,
        dataType: 'number',
        align: 'right',
        defaultWidth: 120,
      },
      {
        id: 'documentValue',
        header: t('tenant.handelMagazyn.warehouse.columns.documentValue'),
        accessor: (row) => row.documentValue,
        dataType: 'number',
        align: 'right',
        defaultWidth: 130,
      },
    );
    return base;
  }, [t, withPositions, navigate]);

  function updateParams(patch: Partial<WarehouseReportParams>) {
    setSearchParams(toWarehouseSearchParams({ ...params, ...patch }));
  }

  const groups = useMemo(() => {
    if (!aggregate.buckets) return undefined;
    return aggregate.buckets.map((bucket) => ({
      key: bucket.key,
      label: (
        <span className="font-medium text-text-primary">
          {bucketLabel(bucket.label, bucket.labelKey, t)}
        </span>
      ),
      count: bucket.count,
      rows: bucket.rows,
      summary: <MoneyLines money={bucket.totals} withQuantity={withPositions} />,
    }));
  }, [aggregate.buckets, t, withPositions]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  useEffect(() => {
    setExpanded(new Set((aggregate.buckets ?? []).map((b) => b.key)));
  }, [aggregate.buckets]);

  const effectiveExpanded = printAll
    ? new Set((aggregate.buckets ?? []).map((b) => b.key))
    : expanded;

  function print() {
    setPrintAll(true);
    requestAnimationFrame(() => {
      window.print();
      setPrintAll(false);
    });
  }

  function exportCsv() {
    const sections = aggregate.buckets
      ? aggregate.buckets.map((b) => ({
          label: bucketLabel(b.label, b.labelKey, t),
          rows: b.rows,
          totals: b.totals,
        }))
      : [{ label: null, rows: aggregate.rows, totals: aggregate.grandTotals }];
    downloadCsv(
      `magazyn-${params.dateFrom}_${params.dateTo}.csv`,
      warehouseReportToCsv({
        headers: columns.map((c) => String(c.header)),
        sections,
        grandTotals: aggregate.grandTotals,
        totalsLabel: t('tenant.handelMagazyn.reports.grandTotal'),
        withPositions,
      }),
    );
  }

  const errorInfo = useMemo(() => {
    if (!isAxiosError(query.error)) return null;
    const data = query.error.response?.data as { error?: string; message?: string } | undefined;
    return data?.message ?? data?.error ?? null;
  }, [query.error]);

  return (
    <div className="p-4">
      <div className="print-area">
        <div className="mb-3 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-text-primary">{reportTitle}</h1>
            <p className="text-sm text-text-secondary">
              {t('tenant.handelMagazyn.reports.rangeLabel', {
                from: params.dateFrom,
                to: params.dateTo,
              })}
              {report ? ` · ${report.integrationName}` : ''}
            </p>
          </div>
          <div className="no-print flex items-center gap-1">
            <Button variant="secondary" size="sm" onClick={() => setParamsOpen(true)}>
              <SlidersHorizontal className="mr-1.5 h-4 w-4" />
              {t('tenant.handelMagazyn.reports.changeParams')}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              <RefreshCw className={`h-4 w-4 ${query.isFetching ? 'animate-spin' : ''}`} />
            </Button>
            {canPrint && (
              <>
                <Button variant="secondary" size="sm" onClick={exportCsv} disabled={!report}>
                  <Download className="mr-1.5 h-4 w-4" />
                  {t('tenant.handelMagazyn.toolbar.export')}
                </Button>
                <Button variant="secondary" size="sm" onClick={print} disabled={!report}>
                  <Printer className="mr-1.5 h-4 w-4" />
                  {t('tenant.handelMagazyn.toolbar.print')}
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="no-print mb-3 flex items-center gap-2">
          <span className="text-sm text-text-secondary">
            {t('tenant.handelMagazyn.reports.summarizeBy')}
          </span>
          <div className="w-64">
            <Dropdown
              value={params.dim}
              onChange={(value) => {
                const dim = value as HmWarehouseReportDimension;
                updateParams({
                  dim,
                  ...(hmWarehouseDimensionNeedsDetails(dim) ? { details: true } : {}),
                });
              }}
              options={HM_WAREHOUSE_REPORT_DIMENSIONS.map((dim) => ({
                value: dim,
                label: t(`tenant.handelMagazyn.warehouseReports.dimension.${dim}`),
              }))}
              data-testid="hm-warehouse-dimension-live"
            />
          </div>
          {params.settlement === 'unsettled' && (
            <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-900">
              {t('tenant.handelMagazyn.warehouseReports.status.unsettled')}
            </span>
          )}
        </div>

        {errorInfo && (
          <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
            {errorInfo}
          </div>
        )}
        {report?.detailsSkipped && (
          <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {t('tenant.handelMagazyn.reports.detailsSkipped')}
          </div>
        )}
        {report?.truncated && (
          <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {t('tenant.handelMagazyn.reports.truncated')}
          </div>
        )}
        {failed.length > 0 && (
          <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {t('tenant.handelMagazyn.reports.detailsFailed', { count: failed.length })}
          </div>
        )}
        {progress && (
          <div
            className="no-print mb-2 rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-xs text-text-secondary"
            data-testid="hm-warehouse-report-progress"
          >
            {t('tenant.handelMagazyn.reports.detailsProgress', {
              done: progress.done,
              total: progress.total,
            })}
          </div>
        )}

        <div
          className="mb-2 flex flex-wrap items-center gap-x-6 gap-y-1 rounded-md border border-border-primary bg-bg-secondary px-3 py-2"
          data-testid="hm-warehouse-report-totals"
        >
          <span className="text-sm font-semibold text-text-primary">
            {t('tenant.handelMagazyn.reports.grandTotal')}
          </span>
          <span className="text-xs text-text-secondary">
            {withPositions && aggregate.positionCount != null
              ? t('tenant.handelMagazyn.reports.countsWithPositions', {
                  documents: aggregate.documentCount,
                  positions: aggregate.positionCount,
                })
              : t('tenant.handelMagazyn.reports.counts', { documents: aggregate.documentCount })}
          </span>
          <MoneyLines money={aggregate.grandTotals} withQuantity={withPositions} />
        </div>

        <ConfigurableTable<HmWarehouseReportRow>
          tableId="hm_warehouse_report"
          columns={columns}
          data={aggregate.buckets ? [] : aggregate.rows}
          groups={groups}
          expandedGroupKeys={groups ? effectiveExpanded : undefined}
          onToggleGroup={
            groups
              ? (key) =>
                  setExpanded((prev) => {
                    const next = new Set(prev);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  })
              : undefined
          }
          isLoading={query.isLoading}
          getRowId={(row) => `${row.documentId}-${row.positionNo ?? 0}`}
          persistConfig={false}
          configurable={false}
          emptyIcon={Box}
          emptyTitle={t('tenant.handelMagazyn.warehouseReports.empty')}
          footerLabel={t('tenant.handelMagazyn.reports.grandTotal')}
          footerCells={(column) => {
            const money = aggregate.grandTotals;
            if (money.length === 0) return null;
            if (column.id === 'currency') {
              return money.map((m) => <div key={m.currency}>{m.currency}</div>);
            }
            if (column.id === 'quantity') {
              return money.map((m) => <div key={m.currency}>{m.quantity}</div>);
            }
            const field =
              column.id === 'netValue' ? 'net' : column.id === 'documentValue' ? 'value' : null;
            if (!field) return null;
            return money.map((m) => <div key={m.currency}>{m[field]}</div>);
          }}
          testIdPrefix="hm-warehouse-report"
          stickyHeader
          compact
        />
      </div>

      <WarehouseReportParamsModal
        open={paramsOpen}
        initial={params}
        title={reportTitle}
        onClose={() => setParamsOpen(false)}
        onSubmit={(next) => {
          setParamsOpen(false);
          setSearchParams(toWarehouseSearchParams(next));
        }}
      />
    </div>
  );
}

function bucketLabel(label: string | null, labelKey: string | null, t: (key: string) => string) {
  if (labelKey === 'buffer' || labelKey === 'issued') {
    return t(`tenant.handelMagazyn.status.${labelKey}`);
  }
  if (labelKey) return t(`tenant.handelMagazyn.warehouseReports.status.${labelKey}`);
  return label ?? t('tenant.handelMagazyn.reports.noValue');
}

/** Kwoty per waluta; ilość dokładamy tylko w trybie pozycji, bo tylko tam coś znaczy. */
function MoneyLines({ money, withQuantity }: { money: HmWarehouseMoney[]; withQuantity: boolean }) {
  const { t } = useTranslation();
  if (money.length === 0) return <span className="text-xs text-text-tertiary">—</span>;
  return (
    <span className="flex flex-wrap items-center gap-x-4 gap-y-0.5">
      {money.map((m) => (
        <span key={m.currency} className="text-xs text-text-secondary">
          <span className="font-mono text-text-primary">{m.net}</span> {m.currency}
          {withQuantity && (
            <span className="ml-2 text-text-tertiary">
              {t('tenant.handelMagazyn.reports.columns.quantity')} {m.quantity}
            </span>
          )}
        </span>
      ))}
    </span>
  );
}

export default HmWarehouseReport;
